import React from 'react';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import { Box, Stack } from '@mui/material';
import StorageOutlinedIcon from '@mui/icons-material/StorageOutlined';
import SyncAltOutlinedIcon from '@mui/icons-material/SyncAltOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import { useGetListEventDataChannel } from 'services/v1/Tenant/AROEvent/EventDataSourceService';
import { EventDataChannel } from 'types/api/Tenant/EventDataSourceTypes';
import { SubjectItem } from 'types/api/SystemTenant/AROKMS/SubjectTypes';
import { BUTTON_ACTION_CHANNEL_ITEM_KEY, ChannelCard } from './ChannelCard';
import ModalChannelPreviewData, { ModalChannelPreviewDataState } from './ModalChannelPreviewData';

export type ChannelListProps = {
  eventSubject?: SubjectItem;
  onSelectedChannel?: (selectedChannel: EventDataChannel) => void;
};

export function ChannelList(props: ChannelListProps) {
  const { eventSubject, onSelectedChannel } = props;

  const [dataChannelPreviewModal, setDataChannelPreviewModal] = React.useState<ModalChannelPreviewDataState>({
    open: false,
  });
  const { data: eventChannelList } = useGetListEventDataChannel();

  const handleOnSelectChannel = (selectedChannel: EventDataChannel, optionKey: string) => {
    if (optionKey === BUTTON_ACTION_CHANNEL_ITEM_KEY.PREVIEW_DATA) {
      setDataChannelPreviewModal({ open: true, selectedChannel });
    }
    if (optionKey === BUTTON_ACTION_CHANNEL_ITEM_KEY.CONNECT_CHANNEL) {
      handleOnSelectedChannel(selectedChannel);
    }
  };

  const handleOnSelectedChannel = (selectedChannel: EventDataChannel) => {
    onSelectedChannel?.(selectedChannel);
  };

  return (
    <Stack sx={{ flex: 1, overflow: 'auto', bgcolor: '#F8F9FC' }}>
      <Stack px={4} py={3} gap={3} sx={{ maxWidth: 1200, mx: 'auto', width: '100%' }}>
        {/* Hero section */}
        <Stack
          direction='row'
          alignItems='center'
          gap={3}
          sx={{
            p: 3,
            borderRadius: 2,
            bgcolor: '#fff',
            border: '1px solid #E3EBF6',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 52,
              height: 52,
              borderRadius: '14px',
              bgcolor: '#EEF7F3',
              flexShrink: 0,
            }}
          >
            <StorageOutlinedIcon sx={{ fontSize: 26, color: '#42BB93' }} />
          </Box>
          <Stack flex={1} spacing={0.5}>
            <Typography variant='input-label' fontSize={17} fontWeight={800}>
              Data Channel Integration
            </Typography>
            <Typography variant='input-label-gray' fontSize={14} sx={{ lineHeight: 1.5 }}>
              Select a Data Channel to synchronize with your{' '}
              <Box component='span' fontWeight={700} sx={{ color: '#3B4797' }}>
                {eventSubject?.displayName}
              </Box>
              . Connecting a channel enables automatic data import, facilitating a seamless data flow into your system.
            </Typography>
          </Stack>
          <SyncAltOutlinedIcon sx={{ fontSize: 32, color: '#3B4797', flexShrink: 0 }} />
          <Stack
            sx={{
              px: 2.5,
              py: 1.5,
              borderRadius: 2,
              bgcolor: '#EDF0FF',
              border: '1px solid #C7CEED',
              flexShrink: 0,
            }}
          >
            <Typography variant='input-label' fontSize={15} fontWeight={800} sx={{ color: '#3B4797' }}>
              {eventSubject?.displayName}
            </Typography>
            <Typography variant='input-label-gray' fontSize={12}>
              Target Event
            </Typography>
          </Stack>
        </Stack>

        {/* Info note */}
        <Alert
          severity='info'
          variant='outlined'
          icon={<InfoOutlinedIcon sx={{ fontSize: 20 }} />}
          sx={{
            borderRadius: 1.5,
            '& .MuiAlert-message': { fontSize: 13 },
          }}
        >
          The list below contains the data channels available within your organization. If the channel you require is not
          listed, please reach out to your aroTarget administrator for access.
        </Alert>

        {/* Channel cards grid */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: 2.5,
          }}
        >
          {eventChannelList?.data?.map((channelItem) => (
            <ChannelCard key={channelItem.id} channelItem={channelItem} onSelectChannel={handleOnSelectChannel} />
          ))}
        </Box>
      </Stack>
      <ModalChannelPreviewData
        visible={dataChannelPreviewModal.open}
        selectedChannel={dataChannelPreviewModal.selectedChannel}
        onConnectChannel={handleOnSelectedChannel}
        onClose={() => setDataChannelPreviewModal({ open: false, selectedChannel: undefined })}
      />
    </Stack>
  );
}
